/**
 * What a publish changes in the solution's ACTION CODE — the scripts on the
 * Actions page (`services/…` functions and the top-level `.py` config files),
 * which live as version files, not in the node table `solution-diff.ts`
 * compares. An edited action changes what the solution does as surely as an
 * edited node, so Deploy shows it beside them.
 */

/** A file the Actions page manages: a `services/` script or a top-level `.py`. */
export const isActionFile = (path: string): boolean =>
  (path.startsWith('services/') && !path.endsWith('/')) ||
  /^[^/]+\.py$/.test(path);

export type DiffLineKind = 'same' | 'add' | 'rm';

export interface DiffLine {
  kind: DiffLineKind;
  text: string;
}

export interface ActionFileChange {
  path: string;
  /** The file's name, without its folder. */
  name: string;
  change: 'added' | 'edited' | 'removed';
  added: number;
  removed: number;
  /** The changed lines with a little context; empty for a file too large to diff. */
  lines: DiffLine[];
}

/** Past this many lines a side, a file is reported by count, not line by line. */
const MAX_DIFF_LINES = 2_000;
const CONTEXT = 2;

const splitLines = (s: string) => s.replace(/\r\n/g, '\n').split('\n');

/** Line diff by longest common subsequence — action files are small. */
export const diffLines = (before: string, after: string): DiffLine[] => {
  const a = splitLines(before);
  const b = splitLines(after);
  const n = a.length;
  const m = b.length;
  const lcs: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0)
  );
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      lcs[i][j] =
        a[i] === b[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: 'same', text: a[i] });
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      out.push({ kind: 'rm', text: a[i] });
      i += 1;
    } else {
      out.push({ kind: 'add', text: b[j] });
      j += 1;
    }
  }
  while (i < n) out.push({ kind: 'rm', text: a[(i += 1) - 1] });
  while (j < m) out.push({ kind: 'add', text: b[(j += 1) - 1] });
  return out;
};

/** Only the changed lines and `CONTEXT` lines around each, with gaps as "…". */
export const withContext = (lines: DiffLine[]): DiffLine[] => {
  const keep = lines.map(() => false);
  lines.forEach((l, i) => {
    if (l.kind === 'same') return;
    for (
      let k = Math.max(0, i - CONTEXT);
      k <= Math.min(lines.length - 1, i + CONTEXT);
      k += 1
    ) {
      keep[k] = true;
    }
  });
  const out: DiffLine[] = [];
  let folded = false;
  lines.forEach((l, i) => {
    if (keep[i]) {
      out.push(l);
      folded = false;
    } else if (!folded) {
      // One "…" per run of unchanged lines left out.
      out.push({ kind: 'same', text: '…' });
      folded = true;
    }
  });
  return out;
};

const nameOf = (path: string) => path.split('/').pop() || path;

/**
 * The action files that differ between two versions, by path. `before` is
 * null on a first publish (everything is new).
 */
export const diffActionFiles = (
  before: Record<string, string> | null,
  after: Record<string, string>
): ActionFileChange[] => {
  const prev = before ?? {};
  const paths = [...new Set([...Object.keys(prev), ...Object.keys(after)])]
    .filter(isActionFile)
    .sort();
  return paths.flatMap((path): ActionFileChange[] => {
    const was = prev[path];
    const now = after[path];
    if (was === now) return [];
    const change =
      was === undefined ? 'added' : now === undefined ? 'removed' : 'edited';
    const a = was ?? '';
    const b = now ?? '';
    const tooBig =
      splitLines(a).length > MAX_DIFF_LINES ||
      splitLines(b).length > MAX_DIFF_LINES;
    const all = tooBig ? [] : diffLines(a, b);
    const count = (kind: DiffLineKind) =>
      tooBig
        ? splitLines(kind === 'add' ? b : a).length
        : all.filter((l) => l.kind === kind).length;
    return [
      {
        path,
        name: nameOf(path),
        change,
        added: change === 'removed' ? 0 : count('add'),
        removed: change === 'added' ? 0 : count('rm'),
        lines: tooBig ? [] : withContext(all),
      },
    ];
  });
};

/** One line per changed file, for the AI-drafted version notes. */
export const describeActionChanges = (changes: ActionFileChange[]): string =>
  changes
    .map(
      (c) => `Action code ${c.change}: ${c.path} (+${c.added} −${c.removed})`
    )
    .join('\n');
